// زرّان مستقلان تمامًا: «تسجيل الحضور» و«تسجيل الانصراف».
//
// الاستقلال قاعدة معتمدة لا تحسين واجهة: الانصراف **لا** يشترط حضورًا سابقًا ولا يُعطَّل لغيابه،
// والحضور لا يُعطَّل لوجود حضور سابق. كل زر يُعطَّل أثناء طلبه وحده فقط — منعًا للنقر المزدوج،
// لا لفرض ترتيب. ولا يوجد في هذا الملف أي شرط يقرأ حالة الزر الآخر **ليمنع** نقرة.
//
// وما يقرأه من الحالة إنما يقرأه ليقول للموظف أين هو من يومه ويُبرز الخطوة المتوقَّعة بصريًا:
// التوكيد لون وترتيب لا قفل — والزرّ الآخر يبقى ظاهرًا مفعَّلًا في كل الحالات.
//
// والتأكيد قبل كل بصمة (TapConfirm): الحدث لا يُحذف، فالنقرة غير المقصودة تترك أثرًا يُشرح
// ولا يُمحى. والتأكيد سؤال لا شرط: لا يفحص ترتيبًا ولا يمنع نوعًا.
//
// الوقت من الخادم وحده: لا حقل وقت في الجسم (الجسم صارم فأي حقل زائد يُرفض بـ400)، والوقت
// المعروض بعد النجاح هو occurredAt الذي أعاده الخادم لا لحظة محلية.
//
// ولا بيانات جهاز على الإطلاق (قرار المالك D-2): الجسم لا يحمل إلا الموقع أو إعلان غيابه.
//
// والمنطق نفسه في `useTap` — يستعمله زرّ الترويسة السريع أيضًا، فلا توجد نسخة ثانية منه.
import { useState } from 'react';
import type { AttendanceKind, AttendanceToday } from '../lib/api.ts';
import { Alert } from '../components/ui.tsx';
import { cairoTimeAr } from '../lib/format.ts';
import { KIND_AR } from './attendanceLabels.ts';
import { TapConfirm } from './TapConfirm.tsx';
import { useTap } from './useTap.ts';

/** جملة واحدة تقول للموظف أين هو من يومه، بلغته لا بأسماء الحقول. */
/** الجلسة مفتوحة الآن؟ من الخادم، ومع خادمٍ أقدم يُستنتج كما كان */
export const isOpenSession = (t?: AttendanceToday | null): boolean =>
  t?.openSession ?? (t?.checkedIn === true && t.checkedOut !== true);

function stateLine(today?: AttendanceToday | null): { text: string; pending: boolean } {
  if (!today) return { text: 'حالة يومك', pending: false };
  // جلسةٌ مفتوحة تُقال أوّلًا — ولو سبقها انصرافٌ اليوم (رحمه تحضر وتنصرف مرّاتٍ)
  if (isOpenSession(today)) {
    const since = today.lastCheckInAt ?? today.firstCheckInAt;
    if (since) return { text: `أنت مسجَّل حضور منذ ${cairoTimeAr(since)}`, pending: false };
  }
  if (today.checkedOut && today.lastCheckOutAt) {
    return { text: `سجّلت انصرافك الساعة ${cairoTimeAr(today.lastCheckOutAt)}`, pending: false };
  }
  if (today.checkedIn && today.firstCheckInAt) {
    return { text: `أنت مسجَّل حضور منذ ${cairoTimeAr(today.firstCheckInAt)}`, pending: false };
  }
  return { text: 'لم تسجّل حضورك اليوم بعد', pending: true };
}

export function TapButtons(
  { onRecorded, today }: { onRecorded: () => void | Promise<void>; today?: AttendanceToday | null },
) {
  const { busy, error, done, tap } = useTap(onRecorded);
  const [confirm, setConfirm] = useState<AttendanceKind | null>(null);
  const { text, pending } = stateLine(today);
  // الخطوة المتوقَّعة: الحضور ما لم يكن مسجَّلًا حضورًا ولم ينصرف. توكيد بصري لا قفل.
  const expected: AttendanceKind = isOpenSession(today) ? 'CHECK_OUT' : 'CHECK_IN';
  const cls = (k: AttendanceKind) =>
    `${k === expected ? 'btn-primary' : 'btn-ghost'} w-full py-4 text-lg`;

  async function run(kind: AttendanceKind): Promise<void> {
    await tap(kind);
    setConfirm(null);
  }

  return (
    <div className="space-y-3">
      <p className={`text-lg font-semibold ${pending ? 'text-amber-900' : 'text-ink-900'}`}>{text}</p>

      <div className="grid gap-3 sm:grid-cols-2">
        <button type="button" className={cls('CHECK_IN')}
          disabled={busy === 'CHECK_IN'} onClick={() => setConfirm('CHECK_IN')}>
          {busy === 'CHECK_IN' ? 'جارٍ التسجيل…' : 'تسجيل الحضور'}
        </button>
        <button type="button" className={cls('CHECK_OUT')}
          disabled={busy === 'CHECK_OUT'} onClick={() => setConfirm('CHECK_OUT')}>
          {busy === 'CHECK_OUT' ? 'جارٍ التسجيل…' : 'تسجيل الانصراف'}
        </button>
      </div>

      {/* الملاحظة تخصّ صحّة التسجيل وحدها (قرار المالك) — وما كان يُقال عن الموقع حُذف */}
      <p className="text-xs text-ink-500">
        يُسألك النظام تأكيدًا قبل كل تسجيل. والوقت يُسجَّل من <strong>الخادم</strong> بتوقيت
        القاهرة لا من ساعة جهازك.
      </p>

      {done && (
        <Alert kind="success">
          تم تسجيل {KIND_AR[done.kind]} الساعة {cairoTimeAr(done.at)}
        </Alert>
      )}
      {done?.lateNotice && <Alert>⏰ {done.lateNotice}</Alert>}
      {error && <Alert>{error}</Alert>}

      {confirm && (
        <TapConfirm kind={confirm} busy={busy === confirm}
          onCancel={() => setConfirm(null)} onConfirm={() => void run(confirm)} />
      )}
    </div>
  );
}
